import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import MembershipCard from '@/components/MembershipCard';
import type { UserMembership } from '@/src/api/authApi';
import { heldMembership } from '@/src/lib/membershipFixtures';

/**
 * ── /components/membership-card ──────────────────────────────────────────
 *
 * `components/MembershipCard` — the one card both `/my-memberships` and
 * `/my-memberships/[id]` draw. Its tone (current glow, upcoming onyx, past
 * grey) and fade come from `sectionOf()` + `isEnding()`, so each fixture below
 * exists to pin down one tone/opacity combination those can produce.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Local-time ISO, no trailing Z: `formatDateWithYear` reads dates through
// `parseISO`, which treats a Z-less string as local time (see all.tsx's own
// `slotIso`). `toISOString` would shift to UTC and risk landing on the wrong
// calendar day for a date this close to midnight.
const slotIso = (daysOut: number) => {
  const d = new Date();
  d.setDate(d.getDate() + daysOut);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T12:00:00`;
};

const LIST_CARD_STATES: {
  id: string;
  label: string;
  membership: UserMembership;
}[] = [
  {
    id: 'current',
    label: 'current — the Access Pass glow over onyx, full strength',
    membership: {
      ...heldMembership('access_pass', 'Access Pass'),
      activatedAt: slotIso(-48),
    },
  },
  {
    id: 'current-trial',
    label:
      'current, trial — the pink trial glow over onyx, plus the corner TRIAL badge; a one-time pass, so it expires rather than renews',
    membership: {
      ...heldMembership('trial', 'Trial Pass'),
      activatedAt: slotIso(-5),
      renewsAt: null,
      expiresAt: slotIso(25),
      daysRemaining: 25,
    },
  },
  {
    id: 'current-trial-used-up',
    label:
      'current, trial, perks used up — every included perk at remaining 0, so hasUsedUpTrial() adds the band along the foot; the dates stay, since the pass is still technically live',
    membership: {
      ...heldMembership('trial', 'Trial Pass'),
      activatedAt: slotIso(-5),
      renewsAt: null,
      expiresAt: slotIso(25),
      daysRemaining: 25,
      perksUsage: {
        resetsAt: null,
        perks: {
          normal: {
            label: 'Events',
            bookings: 2,
            canBook: false,
            isUnlimited: false,
            limit: 2,
            remaining: 0,
          },
        },
      },
    },
  },
  {
    id: 'current-ending',
    label:
      'current, cancelling — same gradient, faded to ENDING_OPACITY (0.6): isEnding() fires because phase is still "current" and status is "cancelled", even though the membership keeps its benefits until the cycle ends',
    membership: {
      ...heldMembership('premium', 'Access Pass Premium', slotIso(19)),
      activatedAt: slotIso(-220),
      daysRemaining: 19,
    },
  },
  {
    id: 'upcoming',
    label: 'upcoming, pending payment — flat onyx ($brandDark), full opacity',
    membership: {
      id: 'um-upcoming',
      plan: {
        id: 'plan-access_pass',
        name: 'Access Pass',
        tier: 'access_pass',
        kind: 'access_pass',
      },
      status: 'pending',
      source: 'payment',
      phase: 'upcoming',
      activatedAt: null,
      renewsAt: null,
      expiresAt: null,
      daysRemaining: null,
      perksUsage: null,
    },
  },
  {
    id: 'upcoming-cancelled',
    label:
      "upcoming, cancelled before its first charge — sectionOf()'s one override files this under past ($brandGray) rather than onyx: a phase:'upcoming' row never gets a billing cycle behind it, so nothing would ever move it on to 'past' on its own. isEnding() still reads the membership's RAW phase ('upcoming', not sectionOf's overridden 'past'), so this card draws at full opacity, not faded — it's grey but not ending",
    membership: {
      id: 'um-upcoming-cancelled',
      plan: {
        id: 'plan-access_pass',
        name: 'Access Pass',
        tier: 'access_pass',
        kind: 'access_pass',
      },
      status: 'cancelled',
      source: 'payment',
      phase: 'upcoming',
      activatedAt: null,
      renewsAt: null,
      expiresAt: null,
      daysRemaining: null,
      perksUsage: null,
    },
  },
  {
    id: 'past',
    label:
      'past — flat grey ($brandGray), wordmark unchanged; a trial, so it keeps the TRIAL badge but not the glow',
    membership: {
      id: 'um-past',
      plan: {
        id: 'plan-trial',
        name: 'Trial Pass',
        tier: 'trial',
        kind: 'trial',
      },
      status: 'expired',
      source: 'payment',
      phase: 'past',
      activatedAt: slotIso(-60),
      renewsAt: null,
      expiresAt: slotIso(-30),
      daysRemaining: null,
      perksUsage: null,
    },
  },
];

export default function MembershipCardPreview() {
  return (
    <>
      <Head>
        <title>MembershipCard · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          <YStack alignSelf="stretch" gap="$lg">
            {LIST_CARD_STATES.map((state) => (
              <YStack key={state.id} gap="$xs">
                <Text
                  fontFamily="$body"
                  fontSize="$small"
                  color="$textDarkMuted"
                >
                  {state.label}
                </Text>
                <MembershipCard membership={state.membership} />
              </YStack>
            ))}
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
